import { getPointOfInterestMarkerProps } from '../helpers/map-marker';
import { MAX_SERVICE_LOGO_MARKER_ZOOM } from '../config';

import { stationsClusterHaloLayerId } from './useStationLayers';

import { addMapMarkerToMapboxMap } from '@/lib/helpers/mapbox';
import { objectToStringSorted } from '@/lib/utils/objects';

import type {
  CircleLayer,
  GeoJSONSource,
  GeoJSONSourceRaw,
  Map,
  SymbolLayer,
} from 'mapbox-gl';
import type { Feature, FeatureCollection } from 'geojson';

interface UsePointOfInterestLayersReturn {
  /**
   * ID of the symbol layer for the points of interest
   */
  pointsOfInterestLayerId: string;
}

export function usePointOfInterestLayers(
  map: Ref<Map | undefined>,
  pointsOfInterest: Ref<MapPointOfInterest[]>,
  selectedPointOfInterestId: Ref<string | null>
): UsePointOfInterestLayersReturn {
  const { theme } = useClientStore().clientState;

  const pointsOfInterestFeatures = ref<Feature[]>([]);

  const pointsOfInterestSourceId = 'points-of-interest-source';
  const pointsOfInterestLayerId = 'points-of-interest-layer';

  function getPointOfInterestImage(
    pointOfInterest: MapPointOfInterest
  ): string {
    const isSelected = pointOfInterest.id === selectedPointOfInterestId.value;
    return objectToStringSorted(
      getPointOfInterestMarkerProps(pointOfInterest, theme, isSelected)
    );
  }

  function updatePointsOfInterestFeatures(): void {
    pointsOfInterestFeatures.value = pointsOfInterest.value.map(
      pointOfInterest => {
        const markerProps = getPointOfInterestMarkerProps(
          pointOfInterest,
          theme
        );
        addMapMarkerToMapboxMap(map, markerProps);

        const selectedMarkerProps = getPointOfInterestMarkerProps(
          pointOfInterest,
          theme,
          true
        );
        addMapMarkerToMapboxMap(map, selectedMarkerProps);

        const image = getPointOfInterestImage(pointOfInterest);

        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: pointOfInterest.coordinates,
          },
          properties: {
            id: pointOfInterest.id,
            image,
            type: pointOfInterest.type,
            entityType: 'point-of-interest',
          },
        } satisfies Feature;
      }
    );
  }

  function syncPointsOfInterestSource(): void {
    if (!map.value) return;

    const source: GeoJSONSourceRaw = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: pointsOfInterestFeatures.value,
      },
    };

    if (!map.value.getSource(pointsOfInterestSourceId)) {
      map.value.addSource(pointsOfInterestSourceId, source);
    } else {
      (map.value.getSource(pointsOfInterestSourceId) as GeoJSONSource)?.setData(
        source.data as FeatureCollection
      );
    }
  }

  function registerLayers(): void {
    if (!map.value) return;

    // Circle layer, displayed when the map is lowly zoomed
    const circlePointsOfInterestLayerId = 'circle-points-of-interest-layer';

    if (!map.value.getLayer(circlePointsOfInterestLayerId)) {
      const circleLayer: CircleLayer = {
        id: circlePointsOfInterestLayerId,
        type: 'circle',
        source: pointsOfInterestSourceId,
        paint: {
          'circle-radius': 8,
          'circle-stroke-width': 2,
          'circle-stroke-color': theme.colorNeutralLight5,
          'circle-color': [
            'case',
            ['==', ['get', 'type'], 'bike_house'],
            theme.colorSecondaryLight1,
            ['==', ['get', 'type'], 'bike_locker'],
            theme.colorSecondary,
            theme.colorSecondary,
          ],
        },
        minzoom: MAX_SERVICE_LOGO_MARKER_ZOOM + 1,
        maxzoom: 13,
      };
      map.value.addLayer(circleLayer);
    }

    if (!map.value.getLayer(pointsOfInterestLayerId)) {
      const pointsOfInterestLayer: SymbolLayer = {
        id: pointsOfInterestLayerId,
        type: 'symbol',
        source: pointsOfInterestSourceId,
        layout: {
          'icon-image': ['get', 'image'],
          'icon-size': 0.5,
          'icon-ignore-placement': true,
          'icon-allow-overlap': true,
          'icon-anchor': 'bottom',
        },
        minzoom: 13,
      };

      map.value.addLayer(pointsOfInterestLayer);
    }

    // Move the circle layer below the stations cluster halo layer
    if (map.value.getLayer(stationsClusterHaloLayerId)) {
      map.value.moveLayer(
        circlePointsOfInterestLayerId,
        stationsClusterHaloLayerId
      );
    }
  }

  watchImmediate([pointsOfInterest, selectedPointOfInterestId], async () => {
    await until(map).not.toBeUndefined();

    updatePointsOfInterestFeatures();
    syncPointsOfInterestSource();

    registerLayers();
  });

  return {
    pointsOfInterestLayerId,
  };
}
